import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AmbienteUsuario, LoginResponse, TipoUsuario } from '../types/LoginResponse.type';

const AMBIENTE_KEY = 'ambiente-id';

@Injectable({ providedIn: 'root' })
export class LoginService {
    readonly session = signal<LoginResponse | null>(this.restore());
    private readonly ambienteEscolhidoId = signal<string | null>(localStorage.getItem(AMBIENTE_KEY));

    /** Ambiente em uso: o escolhido no seletor, se o usuário ainda tiver acesso; senão um padrão. */
    readonly ambienteSelecionado = computed<AmbienteUsuario | null>(() => {
        const ambientes = this.session()?.ambientes ?? [];
        return ambientes.find(ambiente => ambiente.id === this.ambienteEscolhidoId()) ?? this.ambientePadrao(ambientes);
    });

    constructor(private http: HttpClient) {
        const storage = (event: StorageEvent) => {
            if (event.storageArea && event.storageArea !== localStorage) return;
            if (!event.key || event.key === 'auth-session') this.session.set(this.restore());
        };
        window.addEventListener('storage', storage);
        inject(DestroyRef).onDestroy(() => window.removeEventListener('storage', storage));
    }

    login(email: string, senha: string) {
        return this.http.post<LoginResponse>(`${environment.apiUrl}/auth/login`, { email: email.trim(), senha }).pipe(
            tap(response => {
                localStorage.setItem('auth-session', JSON.stringify(response));
                this.session.set(response);
            })
        );
    }

    logout(): void {
        localStorage.removeItem('auth-session');
        // Remove keys written by the old login implementation as well.
        ['auth-token', 'nomeUsuario', 'emailUsuario', 'roles', 'actions', 'tipo'].forEach(key => localStorage.removeItem(key));
        this.session.set(null);
    }

    isAutenticado(): boolean {
        const token = this.getToken();
        return !!token && !this.isExpired(token);
    }
    getToken(): string { return this.session()?.token ?? ''; }
    getUsuario(): string { return this.session()?.email ?? ''; }
    getTipo(): TipoUsuario | null { return this.session()?.tipo ?? null; }
    getAmbientes(): AmbienteUsuario[] { return this.session()?.ambientes ?? []; }
    getClientSecretKey(): string { return this.ambienteSelecionado()?.clientSecretKey ?? ''; }

    /** Troca o ambiente. A safra pertence à base do cliente, então a escolha anterior deixa de valer. */
    selecionarAmbiente(id: string): void {
        localStorage.setItem(AMBIENTE_KEY, id);
        localStorage.removeItem('safra-id');
        this.ambienteEscolhidoId.set(id);
    }

    /** Recarrega os ambientes do usuário (ex.: chave regenerada ou ambiente novo desde o login). */
    atualizarAmbientes(): Observable<AmbienteUsuario[]> {
        return this.http.get<AmbienteUsuario[]>(`${environment.apiUrl}/auth/ambientes`).pipe(
            tap(ambientes => {
                const atual = this.session();
                if (!atual) return;
                const session = { ...atual, ambientes };
                localStorage.setItem('auth-session', JSON.stringify(session));
                this.session.set(session);
            })
        );
    }

    /** Sem escolha salva: o único ambiente, ou a produção do próprio cliente, ou o primeiro da lista. */
    private ambientePadrao(ambientes: AmbienteUsuario[]): AmbienteUsuario | null {
        if (ambientes.length <= 1) return ambientes[0] ?? null;
        const idCliente = this.payload(this.getToken())?.idCliente;
        const doCliente = ambientes.filter(ambiente => ambiente.idCliente === idCliente);
        return doCliente.find(ambiente => ambiente.tipo === 'PRODUCAO') ?? doCliente[0] ?? ambientes[0];
    }

    private restore(): LoginResponse | null {
        try {
            const session = JSON.parse(localStorage.getItem('auth-session') || 'null') as LoginResponse | null;
            if (!session?.token || !session.tipo) return null;
            if (this.isExpired(session.token)) { localStorage.removeItem('auth-session'); return null; }
            return session;
        } catch { return null; }
    }

    private isExpired(token: string): boolean {
        const payload = this.payload(token);
        return !payload || (!!payload.exp && payload.exp * 1000 <= Date.now());
    }

    private payload(token: string): { exp?: number; idCliente?: string } | null {
        try { return JSON.parse(atob((token.split('.')[1] || '').replace(/-/g, '+').replace(/_/g, '/'))); }
        catch { return null; }
    }
}
